import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import API from '@Services/apiAxios';

/* =======================
   Types
======================= */

export interface DownloadInvoiceResponse {
  url: string;
  msg: string;
}

interface PurchaseInvoiceDownloadState {
  loading: boolean;
  data: DownloadInvoiceResponse | null;
  error: string | null;
}

/* =======================
   Initial State
======================= */

const initialState: PurchaseInvoiceDownloadState = {
  loading: false,
  data: null,
  error: null,
};

/* =======================
   Async Thunk
======================= */

export const purchaseInvoiceIndividualReportDownload = createAsyncThunk<
  DownloadInvoiceResponse,
  string,
  { rejectValue: string }
>('purchaseInvoice/download', async (invoiceNo, { rejectWithValue }) => {
  try {
    const resp = await API.get(`/invoice/purchase-invoice/download?invoiceNumber=${invoiceNo}`);

    return {
      url: resp.data?.url,
      msg: resp.data?.msg,
    };
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || 'Failed to download invoice');
  }
});

/* =======================
   Slice
======================= */

const purchaseInvoiceIndividualReportDownloadSlice = createSlice({
  name: 'purchaseInvoiceIndividualReportDownload',
  initialState,
  reducers: {
    resetPurchaseInvoiceIndividualReportDownloadState: state => {
      state.loading = false;
      state.data = null;
      state.error = null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(purchaseInvoiceIndividualReportDownload.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(purchaseInvoiceIndividualReportDownload.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(purchaseInvoiceIndividualReportDownload.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Something went wrong while downloading';
      });
  },
});

/* =======================
   Exports
======================= */

export const { resetPurchaseInvoiceIndividualReportDownloadState } =
  purchaseInvoiceIndividualReportDownloadSlice.actions;

export default purchaseInvoiceIndividualReportDownloadSlice.reducer;
